import type { FC } from "react";
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid } from "@mui/material";
import type { UpdateTaskRequest } from "./tasksApi";
import AsyncLoadingButton from "../components/asyncLoadingButton/AsyncLoadingButton";
import { useForm } from "react-hook-form";
import dayjs, { ISO_DATE_FORMAT } from "../dayjs";
import FormTextField from "../components/formComponents/FormTextField";
import FormDatePicker from "../components/formComponents/FormDatePicker";
import type { Dayjs } from "dayjs";
import CancelButton from "../components/cancelButton/CancelButton";
import FormTimePicker from "../components/formComponents/FormTimePicker";
import FormSelect from "../components/formComponents/FormSelect";
import { useAuthContext } from "../auth/AuthContext";
import { useAgents } from "../admin/agents/useAgents";

export interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSave: (request: UpdateTaskRequest) => Promise<void>;
  readonly initialValues?: {
    readonly description: string;
    readonly due: { date: string; time?: string };
    readonly assignedUserId: string;
  };
  readonly title: string;
  readonly submitButtonText: string;
}

interface TaskFormState {
  description: string;
  dueDate: Dayjs;
  dueTime?: Dayjs;
  assignedUser: string;
}

const EditTaskDialog: FC<Props> = ({ open, onClose, onSave, initialValues, title, submitButtonText }) => {
  const { user } = useAuthContext();
  const { agents } = useAgents();
  const { control, handleSubmit, reset } = useForm<TaskFormState>({
    defaultValues: {
      description: initialValues?.description ?? "",
      dueDate: initialValues ? dayjs(initialValues.due.date, ISO_DATE_FORMAT) : dayjs().add(1, "day"),
      dueTime: initialValues?.due.time ? dayjs(initialValues.due.time, "HH:mm") : undefined,
      assignedUser: initialValues?.assignedUserId ?? agents.find((a) => a.loginEmail === user?.email)?.id ?? "",
    },
    mode: "onBlur",
  });

  const handleClose = () => {
    onClose();
    reset();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Grid
          container
          spacing={2}
          sx={{
            marginTop: 2,
          }}
        >
          <Grid size={12}>
            <FormTextField
              label="Opis zadania"
              control={control}
              name="description"
              rules={{ required: true }}
              multiline
              minRows={3}
            />
          </Grid>
          <Grid size={6}>
            <FormDatePicker label="Termin (data)" control={control} name="dueDate" fullWidth minDate={dayjs()} />
          </Grid>
          <Grid size={6}>
            <FormTimePicker label="Godzina" control={control} name="dueTime" fullWidth />
          </Grid>
          <Grid size={12}>
            <FormSelect
              label="Przypisane do"
              control={control}
              name="assignedUser"
              rules={{ required: true }}
              options={agents.map((a) => ({ value: a.id, label: a.name }))}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <CancelButton onClick={handleClose} />
        <AsyncLoadingButton
          onClick={handleSubmit(async (s) => {
            await onSave({
              description: s.description,
              due: { date: s.dueDate.format(ISO_DATE_FORMAT), time: s.dueTime ? s.dueTime.format("HH:mm") : undefined },
              assignedUser: s.assignedUser,
            });
            handleClose();
          })}
        >
          {submitButtonText}
        </AsyncLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditTaskDialog;
